import { useState, type FormEvent } from 'react'
import { api, type Antwort, type Quelle } from '../api'
import { antwortTeile } from '../antwort'
import { useAufnahme, type FertigeAufnahme } from '../aufnahme'
import { link } from '../navigation'
import { dauer } from '../tagebuch'

/** Eine Frage ist kurz; nach einer Minute wird die Aufnahme automatisch beendet */
const MAX_FRAGE_MS = 60_000

const BEISPIELE = ['Was weiß ich über Bernd?', 'Wer wohnt neben Ole und Ida?', 'Was steht nächste Woche an?']

interface Eintrag {
  frage: string
  antwort?: Antwort
  fehler?: string
}

function quellenLink(q: Quelle): string {
  if (q.art === 'ICH') return link.ueberMich
  if (q.art === 'PERSON' && q.id) return link.person(q.id)
  if (q.art === 'NOTIZ' && q.id) return link.notiz(q.id)
  if (q.art === 'GETEILT' && q.id) return link.geteilt(q.id)
  return '#termine'
}

const SYMBOL = { ICH: '🙂', NOTIZ: '📝', GETEILT: '💌', PERSON: '👤', TERMINE: '📅' }

/** Verlauf nur im Speicher: Fragen und Antworten werden nirgends abgelegt. */
export function Fragen() {
  const [frage, setFrage] = useState('')
  const [verlauf, setVerlauf] = useState<Eintrag[]>([])
  const [laeuft, setLaeuft] = useState(false)
  const [versteht, setVersteht] = useState(false)
  const [sprachFehler, setSprachFehler] = useState<string | null>(null)

  async function stellen(text: string) {
    const f = text.trim()
    if (!f || laeuft) return
    setLaeuft(true)
    setFrage('')
    setVerlauf((v) => [{ frage: f }, ...v])
    let ergebnis: Eintrag
    try {
      ergebnis = { frage: f, antwort: await api.fragen(f) }
    } catch (e) {
      ergebnis = { frage: f, fehler: (e as Error).message }
    }
    setVerlauf((v) => [ergebnis, ...v.slice(1)])
    setLaeuft(false)
  }

  // useAufnahme haelt den Callback in einer Ref, er sieht also immer den aktuellen Zustand
  const gesprochen = async (a: FertigeAufnahme) => {
    setVersteht(true)
    setSprachFehler(null)
    try {
      const { text } = await api.frageTranskribieren(a.audio, a.typ)
      if (text) await stellen(text)
      else setSprachFehler('Ich habe nichts verstanden. Bitte noch einmal.')
    } catch (e) {
      setSprachFehler((e as Error).message)
    } finally {
      setVersteht(false)
    }
  }
  const aufnahme = useAufnahme(gesprochen, MAX_FRAGE_MS)
  const nimmtAuf = aufnahme.zustand.art === 'laeuft'

  const absenden = (e: FormEvent) => {
    e.preventDefault()
    stellen(frage)
  }

  return (
    <div className="fragen">
      {nimmtAuf ? (
        <div className="frage-eingabe aufnahme-frage" role="status">
          <span className="punkt" aria-hidden="true" />
          <span className="zeit">{dauer(aufnahme.dauerMs)}</span>
          <div className="pegel" aria-hidden="true">
            <div style={{ transform: `scaleX(${Math.max(0.03, aufnahme.pegel)})` }} />
          </div>
          <button className="link" onClick={aufnahme.abbrechen} aria-label="Aufnahme verwerfen">
            ✕
          </button>
          <button onClick={aufnahme.stoppen}>Fragen</button>
        </div>
      ) : (
        <form className="frage-eingabe" onSubmit={absenden}>
          <input
            type="search"
            enterKeyHint="send"
            placeholder={versteht ? 'Ich höre zu …' : 'Frag deine Notizen …'}
            value={frage}
            onChange={(e) => setFrage(e.target.value)}
            maxLength={1000}
            disabled={versteht}
          />
          {frage.trim() ? (
            <button type="submit" disabled={laeuft}>
              Fragen
            </button>
          ) : (
            <button
              type="button"
              className="mikro"
              onClick={() => {
                setSprachFehler(null)
                aufnahme.starten()
              }}
              disabled={laeuft || versteht}
              aria-label="Frage sprechen"
            >
              🎤
            </button>
          )}
        </form>
      )}
      {versteht && <p className="leise klein">Ich verstehe deine Frage …</p>}
      {aufnahme.zustand.art === 'fehler' && (
        <p className="fehler" onClick={aufnahme.fehlerQuittieren}>
          {aufnahme.zustand.meldung}
        </p>
      )}
      {sprachFehler && <p className="fehler">{sprachFehler}</p>}

      {verlauf.length === 0 && (
        <div className="beispiele">
          <p className="leise">Zum Beispiel:</p>
          {BEISPIELE.map((b) => (
            <button key={b} className="zweitrangig" onClick={() => stellen(b)}>
              {b}
            </button>
          ))}
        </div>
      )}

      {verlauf.map((e, i) => (
        <article key={verlauf.length - i} className="karte dialog">
          <p className="gefragt">{e.frage}</p>
          {!e.antwort && !e.fehler && <p className="leise">Ich schaue in deinen Notizen nach …</p>}
          {e.fehler && <p className="fehler">{e.fehler}</p>}
          {e.antwort && <AntwortAnzeige antwort={e.antwort} />}
        </article>
      ))}
    </div>
  )
}

export function AntwortAnzeige({ antwort }: { antwort: Antwort }) {
  const nachNr = new Map(antwort.quellen.map((q) => [q.nr, q]))
  return (
    <>
      <p className="antwort">
        {antwortTeile(antwort.antwort, new Set(nachNr.keys())).map((t, i) =>
          'text' in t ? (
            <span key={i}>{t.text}</span>
          ) : (
            <a key={i} className="verweis" href={quellenLink(nachNr.get(t.verweis)!)}>
              {t.verweis}
            </a>
          ),
        )}
      </p>
      {antwort.quellen.length > 0 && (
        <ol className="quellen">
          {antwort.quellen.map((q) => (
            <li key={q.nr}>
              <a href={quellenLink(q)}>
                <span className="nr">{q.nr}</span>
                <span>
                  {SYMBOL[q.art]} <strong>{q.titel}</strong>
                  {q.auszug && <span className="leise"> – {q.auszug}</span>}
                </span>
              </a>
            </li>
          ))}
        </ol>
      )}
    </>
  )
}
